import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Users, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { toast } from 'sonner';

import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EventStatusBadge } from '@/components/events/EventStatusBadge';
import { EventTypeBadge } from '@/components/events/EventTypeBadge';
import { EventBookingsList } from '@/components/events/EventBookingsList';
import { EventOccurrencesList } from '@/components/events/EventOccurrencesList';
import { EventEmailHistorySection } from '@/features/activity/EventEmailHistorySection';
import { useProducer } from '@/hooks/useProducer';
import { useProducerEvent } from '@/hooks/useProducerEvents';
import { useEventOccurrences, useCancelOccurrence } from '@/hooks/useEventOccurrences';
import type { EventStatus, EventType, BookingMode } from '@/types/events';

export default function EventBookingsPage() {
  const { farmId, eventId } = useParams<{ farmId: string; eventId: string }>();
  const { producer, loading: producerLoading } = useProducer(farmId || '');
  const { data: event, isLoading: eventLoading } = useProducerEvent(eventId);
  const { data: occurrences, isLoading: occurrencesLoading } = useEventOccurrences(eventId);
  const cancelOccurrence = useCancelOccurrence();

  if (!farmId || !eventId) return null;

  const isLoading = producerLoading || eventLoading;
  const isRecurring = event?.is_recurring && occurrences && occurrences.length > 0;
  const bookingMode: BookingMode = (event?.booking_mode as BookingMode) ?? 'none';
  const isInterestMode = bookingMode === 'none';
  const sectionLabel = isInterestMode ? 'Intressen' : 'Bokningar';

  const handleCancelOccurrence = async (occurrenceId: string) => {
    try {
      await cancelOccurrence.mutateAsync({ id: occurrenceId, eventId: eventId! });
      toast.success('Tillfället har ställts in');
    } catch (error) {
      console.error('Failed to cancel occurrence:', error);
      toast.error('Kunde inte ställa in tillfället');
    }
  };

  if (isLoading) {
    return (
      <ProducerLayout farmId={farmId} producerName={producer?.name} title="Laddar...">
        <div className="space-y-6 max-w-4xl mx-auto">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </ProducerLayout>
    );
  }

  if (!event) {
    return (
      <ProducerLayout farmId={farmId} producerName={producer?.name} title="Aktiviteten hittades inte">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Aktiviteten kunde inte hittas.</p>
          <Button variant="link" asChild className="mt-4">
            <Link to={`/producer/farm/${farmId}/events`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till aktiviteter
            </Link>
          </Button>
        </div>
      </ProducerLayout>
    );
  }

  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);

  return (
    <ProducerLayout
      farmId={farmId}
      producerName={producer?.name}
      title={`${sectionLabel} – ${event.title}`}
    >
      <div data-testid="producer-bookings-page" className="space-y-6 max-w-4xl mx-auto">
        {/* Header with back link */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/producer/farm/${farmId}/events`}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="flex-1">
            <h1 data-testid="producer-bookings-title" className="text-2xl font-bold">{event.title}</h1>
            <p data-testid="producer-bookings-summary" className="text-muted-foreground text-sm">
              {isRecurring ? `Tillfällen & ${sectionLabel.toLowerCase()}` : sectionLabel}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to={`/producer/farm/${farmId}/events/${eventId}/edit`}>
              Redigera aktivitet
            </Link>
          </Button>
        </div>

        {/* Event summary card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <EventTypeBadge type={event.event_type as EventType} />
                <EventStatusBadge status={event.status as EventStatus} />
              </div>
              {isRecurring ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CalendarDays className="h-4 w-4" />
                  <span>{occurrences.length} tillfällen</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {format(startDate, 'd MMMM yyyy, HH:mm', { locale: sv })}
                    {' – '}
                    {format(endDate, 'HH:mm', { locale: sv })}
                  </span>
                </div>
              )}
              {event.use_producer_address ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>Gårdens adress</span>
                </div>
              ) : event.custom_address && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{event.custom_address}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>Max {event.max_spots} platser</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for recurring events, simple list for single events */}
        {isRecurring ? (
          <Tabs defaultValue="occurrences" className="space-y-4">
            <TabsList>
              <TabsTrigger value="occurrences">
                <CalendarDays className="h-4 w-4 mr-2" />
                Tillfällen
              </TabsTrigger>
              <TabsTrigger value="bookings">
                <Users className="h-4 w-4 mr-2" />
                Alla {sectionLabel.toLowerCase()}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="occurrences">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tillfällen</CardTitle>
                </CardHeader>
                <CardContent>
                  <EventOccurrencesList
                    occurrences={occurrences}
                    maxSpots={event.max_spots}
                    onCancelOccurrence={handleCancelOccurrence}
                    isCancelling={cancelOccurrence.isPending}
                  />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="bookings">
              <EventBookingsList eventId={eventId} maxSpots={event.max_spots} />
            </TabsContent>
          </Tabs>
        ) : (
          <EventBookingsList eventId={eventId} maxSpots={event.max_spots} />
        )}

        {/* Email history – collapsed by default */}
        <EventEmailHistorySection eventId={eventId} />
      </div>
    </ProducerLayout>
  );
}